import React, { useEffect, useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Icon from '@amzn/meridian/icon';
import { Profile } from '../../Types/Profile';
import DataSource from '../../api';
import DataState from '../../Types/DataState';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import { SortDirection } from '../../Types/SortDirection';
import { computeColumnWidthAsPercent } from '../Team/Table/TimeTrends/helper';
import Pagination from '@amzn/meridian/pagination';
import {
  formatGoalValueWithUnit,
  getFullNameFromUserID,
  getMetricStringIdAndDefaultMessage,
  getMonthNameFromPeriodValue,
  goalOutcomeMap,
  goalSort,
  showColumn,
  GoalTableColumn,
  GoalTab,
  GoalPage,
  filterCCXGoals,
} from './helper';
import Text from '@amzn/meridian/text';
import chatTokens from '@amzn/meridian-tokens/base/icon/chat';
import {
  goalMetricMetadataMap,
  GoalOutcome,
  GoalPeriodType,
  GoalStatus,
} from '@amzn/aet-galactus-kit';
import Tag from '@amzn/meridian/tag';
import Loader from '@amzn/meridian/loader';
import Link from '@amzn/meridian/link';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Tooltip from '@amzn/meridian/tooltip';
import Alert from '@amzn/meridian/alert';
import { GoalDisplay, goalSortableColumns } from './types';
import GoalJustificationModal from './GoalJustificationModal';
import { renderUrl, urls } from '../../Util';
import GoalCoachingsModal from './GoalCoachingsModal';
import GoalCommentsModal from '../GoalComments/GoalCommentsModal';
import Box from '@amzn/meridian/box';
import { StateContext } from '../../GlobalStateStore';
import { Page } from '../../Util';
import useCustomTracking from '../../Util/useCustomTracking';
import { useNavigate } from 'react-router';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import { NoData } from '../../Util/NoData';

export interface GoalsProps {
  profile: Profile;
  // pass CSA profile if lockedGoalAssociate
  // pass TM profile if !lockedGoalAssociate
  // profile belongs to the displayed user
  cscEntityPrefix: string;
  lockedGoalAssociate?: string;
  changeFlag: boolean;
  page: GoalPage;
}

const Goals: React.FC<GoalsProps> = ({
  profile,
  cscEntityPrefix,
  lockedGoalAssociate,
  changeFlag,
  page,
}: GoalsProps): React.ReactElement => {
  const itemsPerPage = 10;

  const goalSettingTeamEnabled = isFeatureEnabled(Feature.GOAL_SETTING_TEAM);

  const navigate = useNavigate();

  const intl = useIntl();
  const apiNaws = DataSource.getDataFeed('NAWS');
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUserID, requesterUsername, isAdmin } =
    inspireOptions.data;
  const isOnTmPage = isAdmin
    ? profile.directReports.length > 0 ||
      profile.sharedProfileCoachees.length > 0
    : profile.directReports.length > 0;

  const [currentTab, setCurrentTab] = useState<GoalTab>(
    lockedGoalAssociate || !goalSettingTeamEnabled
      ? GoalTab.NO_TAB
      : GoalTab.TEAM
  );
  const [sortColumn, setSortColumn] = useState(goalSortableColumns.PeriodValue);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>(
    SortDirection.DESCENDING
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [teamLevelGoals, setTeamLevelGoals] = useState<GoalDisplay[]>([]);
  const [teamLevelGoalsState, setTeamLevelGoalsState] = useState(
    DataState.LOADING
  );
  const [associateLevelGoals, setAssociateLevelGoals] = useState<GoalDisplay[]>(
    []
  );
  const [associateLevelGoalsState, setAssociateLevelGoalsState] = useState(
    DataState.LOADING
  );

  const [displayGoals, setDisplayGoals] = useState<GoalDisplay[]>([]);
  const [displayGoalsState, setDisplayGoalsState] = useState(
    DataState.INITIALIZED
  );
  const [columnCount, setColumnCount] = useState(7);
  const [selectedGoalForComments, setSelectedGoalForComments] =
    useState<GoalDisplay | null>(null);
  const [openJustificationModal, setOpenJustificationModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<GoalDisplay | null>(null);
  const [isNewJustification, setIsNetJustification] = useState(false);
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;
  const [openCoachingsModal, setOpenCoachingsModal] = useState(false);
  const [justificationModalChangeGoalID, setJustificationModalChangeGoalID] =
    useState('');
  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  const commonClickstreamData = {
    'data-csa-c-tag1': page,
    'data-csa-c-tag2': 'Goal Setting',
    'data-csa-c-tag3': `${
      currentTab == GoalTab.ASSOCIATE ? 'Associate' : 'Team'
    } Tab`,
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  };

  const [GoalTabClickstreamTrackingTag, emitGoalTabClickstreamTags] =
    useCustomTracking(
      `${page}-goal-setting-${
        currentTab == GoalTab.ASSOCIATE ? 'associate' : 'team'
      }-goals-tab`,
      `${page}-goal-setting-${
        currentTab == GoalTab.ASSOCIATE ? 'associate' : 'team'
      }-goals-tab`,
      {
        'data-csa-c-type': 'tab',
        ...commonClickstreamData,
      }
    );
  const [GoalSortClickstreamTrackingTag, emitGoalSortClickstreamTags] =
    useCustomTracking(
      `${page}-goal-setting-table-sort-for-${profile.user.alias}`,
      `${page}-goal-setting-table-sort`,
      {
        'data-csa-c-type': 'table',
        'data-csa-c-sort-column': sortColumn,
        'data-csa-c-sort-order': sortDirection,
        ...commonClickstreamData,
      }
    );

  const showJustificationModal = (
    goalID: string,
    isNewJustification: boolean
  ) => {
    const foundGoal = displayGoals.find(goal => goal.goalID === goalID);
    if (foundGoal) {
      setSelectedGoal(foundGoal);
      setIsNetJustification(isNewJustification);
      setOpenJustificationModal(true);
    }
  };

  const showCoachingsModal = (goalID: string) => {
    const foundGoal = displayGoals.find(goal => goal.goalID === goalID);
    if (foundGoal) {
      setSelectedGoal(foundGoal);
      setOpenCoachingsModal(true);
    }
  };

  const navigateTo = (page: Page, userId: string) => {
    const id = userId.split(':')[1] ?? '';
    if (page === Page.COACHING)
      navigate(renderUrl(`${urls.coachingNotes}/${id}`));
    else if (page === Page.DASHBOARD)
      navigate(renderUrl(`${urls.dashboard}/${id}`));
  };

  const sortGoals = (displayGoals: GoalDisplay[]): GoalDisplay[] => {
    return [...displayGoals].sort(
      goalSort(sortColumn as keyof GoalDisplay, sortDirection)
    );
  };

  useEffect(() => {
    emitGoalSortClickstreamTags();
    setCurrentPage(1);
    const sortedGoals = sortGoals(displayGoals);
    setDisplayGoals(sortedGoals);
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    setTotalPages(Math.ceil(Object.keys(displayGoals).length / itemsPerPage));
  }, [displayGoals]);

  useEffect(() => {
    if (
      profile === undefined ||
      profile.user === undefined ||
      profile.user.userID === ''
    ) {
      return;
    }

    if (!lockedGoalAssociate && goalSettingTeamEnabled) {
      const queryGetTeamLevelGoals = apiNaws.prepareQueryforGetTeamLevelGoals(
        `${cscEntityPrefix}:${profile.user.userID}`
      );

      apiNaws.getTeamLevelGoals(
        queryGetTeamLevelGoals,
        setTeamLevelGoals,
        setTeamLevelGoalsState
      );
    }
    let directReports = profile.directReports.map(report => ({
      userName: report.userName,
      userID: report.userID,
      cscUserID: `${cscEntityPrefix}:${report.userID}`,
      displayName: report.displayName,
    }));
    if (isAdmin) {
      // also show shared profile as direct report for admin user for testing
      directReports = [
        ...directReports,
        ...profile.sharedProfileCoachees.map(report => ({
          userName: report.userName,
          userID: report.userID,
          cscUserID: `${cscEntityPrefix}:${report.userID}`,
          displayName: report.displayName,
        })),
      ];
    }

    const queryGetAssociateLevelGoals =
      apiNaws.prepareQueryforGetAssociateLevelGoals(
        `${cscEntityPrefix}:${profile.user.userID}`,
        lockedGoalAssociate
          ? [`${cscEntityPrefix}:${profile.user.userID}`]
          : [
              ...new Set(
                directReports.map(
                  report => `${cscEntityPrefix}:${report.userID}`
                )
              ),
            ]
      );

    apiNaws.getAssociateLevelGoals(
      queryGetAssociateLevelGoals,
      setAssociateLevelGoals,
      setAssociateLevelGoalsState
    );
  }, [profile, changeFlag, justificationModalChangeGoalID]);

  useEffect(() => {
    let count = 0;
    for (const column in GoalTableColumn) {
      count += Number(
        showColumn(
          column as GoalTableColumn,
          currentTab,
          isTm || isAdmin,
          goalSettingTeamEnabled,
          isOnTmPage
        )
      );
    }
    setColumnCount(count);
    setCurrentPage(1);
  }, [currentTab]);

  useEffect(() => {
    if (currentTab === GoalTab.TEAM) {
      // team level tab on TM page
      setDisplayGoalsState(teamLevelGoalsState);
      if (teamLevelGoalsState === DataState.LOADED) {
        // Filter out CCX goals when enableCCXRemoval is enabled
        const filteredGoals = filterCCXGoals(teamLevelGoals);
        setDisplayGoals(sortGoals(filteredGoals));
      }
    } else {
      // associate tab on TM page
      // default on CSA page
      setDisplayGoalsState(associateLevelGoalsState);
      if (associateLevelGoalsState === DataState.LOADED) {
        const updatedAssociateLevelGoals = associateLevelGoals.map(goal => ({
          ...goal,
          associate: getFullNameFromUserID(goal.userID.split(':')[1], profile),
        }));
        // Filter out CCX goals when enableCCXRemoval is enabled
        const filteredGoals = filterCCXGoals(updatedAssociateLevelGoals);
        setDisplayGoals(sortGoals(filteredGoals));
      }
    }
  }, [currentTab, teamLevelGoalsState, associateLevelGoalsState]);

  return (
    <>
      {selectedGoal && (
        <>
          <GoalJustificationModal
            cscEntityPrefix={cscEntityPrefix}
            isOpen={openJustificationModal}
            setIsOpen={setOpenJustificationModal}
            goal={selectedGoal}
            isNewJustification={isNewJustification}
            changeFlag={setJustificationModalChangeGoalID}
            page={page}
            tab={currentTab}
          />
          <GoalCoachingsModal
            isOpen={openCoachingsModal}
            setIsOpen={() => setOpenCoachingsModal}
            goal={selectedGoal}
            page={page}
          />
        </>
      )}
      <Column width='100%' spacing='300' spacingInset='300 none'>
        {!lockedGoalAssociate && goalSettingTeamEnabled && (
          <Row alignmentHorizontal='start'>
            <GoalTabClickstreamTrackingTag />
            <TabGroup
              value={currentTab}
              onChange={tab => {
                setCurrentTab(tab);
                emitGoalTabClickstreamTags();
              }}
            >
              <Tab value={GoalTab.TEAM} key={GoalTab.TEAM} data-cy='team-goals'>
                <FormattedMessage
                  id='cs_perfportal_team'
                  defaultMessage='Team'
                />
              </Tab>
              <Tab
                value={GoalTab.ASSOCIATE}
                key={GoalTab.ASSOCIATE}
                data-cy='associate-goals'
              >
                <FormattedMessage
                  id='cs_perfportal_associate'
                  defaultMessage='Associate'
                />
              </Tab>
            </TabGroup>
          </Row>
        )}
        <Row width='100%'>
          {displayGoalsState === DataState.ERROR ? (
            <Alert type='error'>
              <FormattedMessage
                id='cs_perfportal_error_reason_1'
                defaultMessage='Your credentials may be incorrect or inadequate.'
              />
            </Alert>
          ) : displayGoalsState === DataState.LOADING ||
            displayGoalsState === DataState.INITIALIZED ? (
            <Box width='100%'>
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' size='large' />
              </div>
            </Box>
          ) : displayGoals.length === 0 ? (
            <NoData />
          ) : (
            <>
              <GoalSortClickstreamTrackingTag />
              <Table
                headerRows={1}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                showDividers={true}
                spacing='small'
              >
                <TableRow>
                  {showColumn(
                    GoalTableColumn.ASSOCIATE,
                    currentTab,
                    isTm || isAdmin,
                    goalSettingTeamEnabled,
                    isOnTmPage
                  ) && (
                    <TableCell
                      sortColumn={goalSortableColumns.Associate}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_associate'
                        defaultMessage='Associate'
                      />
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.GOAL) && (
                    <TableCell
                      sortColumn={goalSortableColumns.Metric}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_goal'
                        defaultMessage='Goal'
                      />
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.TARGET) && (
                    <TableCell
                      sortColumn={goalSortableColumns.TargetValue}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_goal_target'
                        defaultMessage='Target'
                      />
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.TIME_RANGE) && (
                    <TableCell
                      sortColumn={goalSortableColumns.PeriodValue}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_timeRange'
                        defaultMessage='Time Range'
                      />
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.STATUS) && (
                    <TableCell
                      sortColumn={goalSortableColumns.Status}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_status'
                        defaultMessage='Status'
                        description='Status for Contacts'
                      />
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.OUTCOME) && (
                    <TableCell
                      sortColumn={goalSortableColumns.Outcome}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_outcome'
                        defaultMessage='Outcome'
                      />{' '}
                      <Tooltip
                        position='bottom'
                        title={intl.formatMessage({
                          id: 'cs_perfportal_goal_outcome_hover',
                          defaultMessage:
                            "Metrics used to evaluate the outcome can update for up to 7 days past the goal's end date. Check back after [GoalEndDate+7] for any updates to this outcome.",
                        })}
                        id='outcomeTooltip'
                      >
                        <Icon tokens={infoKnockoutTokens} />
                      </Tooltip>
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.CREATED_BY) && (
                    <TableCell
                      sortColumn={goalSortableColumns.CreatedBy}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_coaching_createdBy'
                        defaultMessage='Created by'
                      />
                    </TableCell>
                  )}
                  {showColumn(
                    GoalTableColumn.COACHING,
                    currentTab,
                    isTm || isAdmin
                  ) && (
                    <TableCell
                      sortColumn={goalSortableColumns.Coaching}
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_coaching'
                        defaultMessage='Coaching'
                      />
                    </TableCell>
                  )}
                  {showColumn(GoalTableColumn.COMMENTS) && (
                    <TableCell
                      width={computeColumnWidthAsPercent(columnCount)}
                      alignmentVertical='bottom'
                      alignmentHorizontal='end'
                      data-cy='goal-table-header'
                    >
                      <FormattedMessage
                        id='cs_perfportal_coaching_history_comments'
                        defaultMessage='Comments'
                      />
                    </TableCell>
                  )}
                </TableRow>
                {displayGoals
                  .slice(firstVisibleIndex, lastVisibleIndex)
                  .map((goal, index) => {
                    return (
                      <TableRow key={index}>
                        {showColumn(
                          GoalTableColumn.ASSOCIATE,
                          currentTab,
                          isTm || isAdmin,
                          goalSettingTeamEnabled,
                          isOnTmPage
                        ) && (
                          <TableCell>
                            <Link
                              type='secondary'
                              data-testid={`associate-${index}-${goal.goalID}`}
                              onClick={() =>
                                navigateTo(Page.DASHBOARD, goal.userID)
                              }
                            >
                              {goal.associate}
                              <br />
                              {`(${goal.userName})`}
                            </Link>
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.GOAL) && (
                          <TableCell>
                            <Text
                              type='b300'
                              data-testid={`goal-${index}-${goal.goalID}`}
                            >
                              {intl.formatMessage({
                                ...getMetricStringIdAndDefaultMessage(
                                  goal.metric
                                ),
                              })}
                            </Text>

                            <Text
                              type='b200'
                              data-testid={`description-${index}-${goal.goalID}`}
                            >
                              {goal.description}
                            </Text>
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.TARGET) && (
                          <TableCell>
                            <Text
                              type='b300'
                              data-testid={`target-${goal.goalID}`}
                            >
                              {formatGoalValueWithUnit(goal, intl)}
                            </Text>
                            <Text
                              type='b200'
                              data-testid={`target-eval-${goal.goalID}`}
                            >
                              {goalMetricMetadataMap[goal.metric]
                                ?.isHigherBetter
                                ? intl.formatMessage({
                                    id: 'cs_perfportal_greater_than_or_equal_to',
                                    defaultMessage: 'Greater than or equal to',
                                  })
                                : null}

                              {!goalMetricMetadataMap[goal.metric]
                                ?.isHigherBetter
                                ? intl.formatMessage({
                                    id: 'cs_perfportal_less_than_or_equal_to',
                                    defaultMessage: 'Less than or equal to',
                                  })
                                : null}
                            </Text>
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.TIME_RANGE) && (
                          <TableCell>
                            <Text
                              type='b300'
                              data-testid={`time-range-${goal.goalID}`}
                            >
                              {goal.periodType === GoalPeriodType.Month && (
                                <>
                                  {goal.periodValue.split('-')[0]}{' '}
                                  {intl.formatMessage({
                                    id: `cs_perfportal_time_month_${getMonthNameFromPeriodValue(
                                      goal.periodValue
                                    ).toLowerCase()}`,
                                    defaultMessage: getMonthNameFromPeriodValue(
                                      goal.periodValue
                                    ),
                                  })}
                                </>
                              )}
                              {goal.periodType === GoalPeriodType.Week &&
                                goal.periodValue}
                            </Text>
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.STATUS) && (
                          <TableCell>
                            <Text
                              type='b300'
                              data-testid={`status-${goal.goalID}`}
                            >
                              {goal.status === GoalStatus.Open && (
                                <FormattedMessage
                                  id='cs_perfportal_status_open'
                                  defaultMessage='Open'
                                />
                              )}
                              {goal.status === GoalStatus.Closed && (
                                <FormattedMessage
                                  id='cs_perfportal_coaching_status_closed'
                                  defaultMessage='Closed'
                                />
                              )}
                            </Text>
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.OUTCOME) && (
                          <TableCell>
                            <Text
                              type='b300'
                              data-testid={`outcome-${goal.goalID}`}
                            >
                              {goal.status === GoalStatus.Closed &&
                                (!goal.outcome ? (
                                  <Tag type='neutral'>
                                    {intl.formatMessage({
                                      id: 'cs_perfportal_goal_outcome_evaluating',
                                      defaultMessage: 'Evaluating',
                                    })}
                                  </Tag>
                                ) : (
                                  <Tag type={goalOutcomeMap[goal.outcome]}>
                                    {intl.formatMessage({
                                      id: `cs_perfportal_goal_outcome_${goal.outcome.toLowerCase()}`,
                                      defaultMessage: goal.outcome,
                                    })}
                                  </Tag>
                                ))}
                              {isTm &&
                              goal.status === GoalStatus.Closed &&
                              goal.outcome === GoalOutcome.Unmet ? (
                                goal.justification === null ? (
                                  <Text type='b200'>
                                    <Link
                                      type='primary'
                                      onClick={() =>
                                        showJustificationModal(
                                          goal.goalID,
                                          true
                                        )
                                      }
                                      data-csa-c-type='button'
                                      data-csa-c-slot-id={`${page}-goal-setting-give-details`}
                                      data-csa-c-content-id={`${page}-goal-setting-give-details-for-${profile.user.alias}-${goal.goalID}`}
                                      {...commonClickstreamData}
                                    >
                                      <FormattedMessage
                                        id='cs_perfportal_goal_justification_give_details'
                                        defaultMessage='Give Details'
                                      ></FormattedMessage>
                                    </Link>
                                  </Text>
                                ) : (
                                  <Text type='b200'>
                                    <Link
                                      type='primary'
                                      onClick={() =>
                                        showJustificationModal(
                                          goal.goalID,
                                          false
                                        )
                                      }
                                      data-csa-c-type='button'
                                      data-csa-c-slot-id={`${page}-goal-setting-view-details`}
                                      data-csa-c-content-id={`${page}-goal-setting-view-details-for-${profile.user.alias}-${goal.goalID}`}
                                      {...commonClickstreamData}
                                    >
                                      <FormattedMessage
                                        id='cs_perfportal_goal_justification_view_details'
                                        defaultMessage='View Details'
                                      ></FormattedMessage>
                                    </Link>
                                  </Text>
                                )
                              ) : (
                                <></>
                              )}
                            </Text>
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.CREATED_BY) && (
                          <TableCell>
                            <Text
                              type='b300'
                              data-testid={`created-by-${goal.goalID}`}
                            >
                              {goal.createdBy}
                            </Text>
                          </TableCell>
                        )}
                        {showColumn(
                          GoalTableColumn.COACHING,
                          currentTab,
                          isTm || isAdmin
                        ) && (
                          <TableCell>
                            {goal.status === GoalStatus.Open &&
                              goal.coachingIDs?.length === 0 && (
                                <Link
                                  type='secondary'
                                  onClick={() =>
                                    navigateTo(Page.COACHING, goal.userID)
                                  }
                                  data-csa-c-type='button'
                                  data-csa-c-slot-id={`${page}-goal-setting-add-a-coaching`}
                                  data-csa-c-content-id={`${page}-goal-setting-add-a-coaching-for-${profile.user.alias}-${goal.goalID}`}
                                  {...commonClickstreamData}
                                >
                                  <FormattedMessage
                                    id='cs_perfportal_goal_add_a_coaching'
                                    defaultMessage='Add a Coaching'
                                  />
                                </Link>
                              )}
                            {goal.coachingIDs &&
                              goal.coachingIDs?.length > 0 && (
                                <Link
                                  type='secondary'
                                  onClick={() =>
                                    showCoachingsModal(goal.goalID)
                                  }
                                  data-csa-c-type='button'
                                  data-csa-c-slot-id={`${page}-goal-setting-open-attached-coachings`}
                                  data-csa-c-content-id={`${page}-goal-setting-open-attached-coachings-for-${profile.user.alias}-${goal.goalID}`}
                                  {...commonClickstreamData}
                                >
                                  {goal.coachingIDs?.length === 1 ? (
                                    <FormattedMessage
                                      id='cs_perfportal_goal_one_coaching'
                                      defaultMessage='1 Coaching'
                                    />
                                  ) : (
                                    <FormattedMessage
                                      id='cs_perfportal_goal_coachings'
                                      defaultMessage='{count} Coachings'
                                      values={{
                                        count: goal.coachingIDs?.length,
                                      }}
                                    />
                                  )}
                                </Link>
                              )}
                          </TableCell>
                        )}
                        {showColumn(GoalTableColumn.COMMENTS) && (
                          <TableCell alignmentHorizontal='end'>
                            <Tooltip
                              position='start'
                              title={intl.formatMessage({
                                id: 'cs_perfportal_goal_add_or_view_comments_tooltip',
                                defaultMessage: 'Add or view comments',
                              })}
                            >
                              <Button
                                data-csa-c-type='button'
                                data-csa-c-slot-id={`${page}-goal-setting-open-comments`}
                                data-csa-c-content-id={`${page}-goal-setting-open-comments-for-${profile.user.alias}-${goal.goalID}`}
                                {...commonClickstreamData}
                                data-cy='comment-button'
                                data-testid={`comments-${goal.goalID}`}
                                propagateClickEvent={false}
                                type='icon'
                                onClick={() => {
                                  setSelectedGoalForComments(goal);
                                }}
                              >
                                <Icon tokens={chatTokens} />
                              </Button>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
              </Table>
            </>
          )}
        </Row>
        <GoalCommentsModal
          profile={profile}
          selectedGoal={selectedGoalForComments}
          onClose={() => setSelectedGoalForComments(null)}
          page={page}
          tab={currentTab}
        ></GoalCommentsModal>
        {totalPages > 1 && (
          <Row alignmentHorizontal='end' data-cy='pagination'>
            <Pagination
              showSkipArrows={true}
              numberOfPages={totalPages}
              onChange={setCurrentPage}
              currentPage={currentPage}
            />
          </Row>
        )}
      </Column>
    </>
  );
};
export default Goals;
